import { z } from 'zod';
import { BaseResponseSchema, dateSchema, emailSchema, newPasswordSchema, passwordSchema, uuidSchema, } from './primitives.js';
// ==================== Invoice Schemas ====================
export const InvoiceStatusSchema = z.enum([
    'draft',
    'sent',
    'paid',
    'overdue',
    'cancelled'
]);
export const InvoiceLineItemTypeSchema = z.enum([
    'base_subscription',
    'storage_overage',
    'axon_gateways',
    'additional_users',
    'wafer_processing',
    'custom_charge',
    'discount'
]);
export const InvoiceLineItemSchema = z.object({
    type: InvoiceLineItemTypeSchema,
    description: z.string().min(1).max(500),
    quantity: z.coerce.number().min(0),
    unit: z.string().max(50).optional(),
    unit_price: z.coerce.number(),
    amount: z.coerce.number(),
    metadata: z.record(z.unknown()).optional()
});
export const InvoiceSchema = z.object({
    id: uuidSchema,
    invoiceNumber: z.string().min(1).max(50),
    tenantId: uuidSchema,
    subscriptionId: uuidSchema,
    status: InvoiceStatusSchema,
    // Line items and amounts
    lineItems: z.array(InvoiceLineItemSchema),
    subtotal: z.coerce.number().min(0),
    taxRate: z.coerce.number().min(0).max(1).default(0),
    taxAmount: z.coerce.number().min(0).default(0),
    discountAmount: z.coerce.number().min(0).default(0),
    creditApplied: z.coerce.number().min(0).default(0),
    total: z.coerce.number().min(0),
    // Billing period
    periodStart: z.coerce.date(),
    periodEnd: z.coerce.date(),
    // Currency
    currency: z.string().length(3).default('USD'),
    // Dates
    issueDate: z.coerce.date(),
    dueDate: z.coerce.date(),
    paidAt: z.coerce.date().nullable().optional(),
    sentAt: z.coerce.date().nullable().optional(),
    cancelledAt: z.coerce.date().nullable().optional(),
    // Stripe integration
    stripeInvoiceId: z.string().max(255).nullable().optional(),
    stripePaymentIntentId: z.string().max(255).nullable().optional(),
    // Notes and metadata
    notes: z.string().max(2000).nullable().optional(),
    internalNotes: z.string().max(2000).nullable().optional(),
    // Audit fields
    createdBy: uuidSchema.nullable().optional(),
    sentBy: uuidSchema.nullable().optional(),
    cancelledBy: uuidSchema.nullable().optional(),
    cancellationReason: z.string().max(500).nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date()
});
export const CreateInvoiceRequestSchema = z.object({
    tenantId: uuidSchema,
    subscriptionId: uuidSchema,
    periodStart: z.coerce.date(),
    periodEnd: z.coerce.date(),
    issueDate: z.coerce.date().optional(),
    dueDate: z.coerce.date().optional(),
    notes: z.string().max(2000).optional(),
    internalNotes: z.string().max(2000).optional()
});
export const GenerateInvoiceRequestSchema = z.object({
    subscriptionId: uuidSchema,
    periodStart: z.coerce.date(),
    periodEnd: z.coerce.date(),
    usageData: z.object({
        storageUsedTb: z.coerce.number().min(0),
        activeAxonGateways: z.number().int().min(0),
        activeUsers: z.number().int().min(0),
        wafersProcessed: z.number().int().min(0).optional()
    }),
    issueDate: z.coerce.date().optional(),
    dueDate: z.coerce.date().optional(),
    notes: z.string().max(2000).optional(),
    autoSend: z.boolean().default(false)
});
export const ListInvoicesRequestSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    status: InvoiceStatusSchema.optional(),
    tenantId: uuidSchema.optional(),
    subscriptionId: uuidSchema.optional(),
    startDate: z.coerce.date().optional(),
    endDate: z.coerce.date().optional(),
    search: z.string().max(200).optional()
});
export const UpdateInvoiceRequestSchema = z.object({
    status: InvoiceStatusSchema.optional(),
    notes: z.string().max(2000).optional(),
    internalNotes: z.string().max(2000).optional(),
    dueDate: z.coerce.date().optional()
});
export const MarkInvoicePaidRequestSchema = z.object({
    paidAt: z.coerce.date().optional(),
    stripePaymentIntentId: z.string().max(255).optional(),
    notes: z.string().max(500).optional()
});
export const CancelInvoiceRequestSchema = z.object({
    reason: z.string().min(1).max(500),
    notes: z.string().max(500).optional()
});
export const AuthRequestSchema = z.object({
    email: emailSchema,
    password: passwordSchema
});
export const LoginRequestSchema = AuthRequestSchema.extend({
    rememberMe: z.boolean().optional(),
});
export const TenantLoginRequestSchema = LoginRequestSchema.extend({
    tenantId: uuidSchema,
});
export const UnifiedAuthRequestSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    rememberMe: z.boolean().optional(),
});
export const ChangePasswordRequestSchema = z.object({
    currentPassword: passwordSchema,
    newPassword: newPasswordSchema
});
export const InvoiceResponseSchema = z.object({
    id: z.string(),
    number: z.string().optional(),
    status: z.string(),
    amountDue: z.string(),
    amountPaid: z.string(),
    amountRemaining: z.string(),
    currency: z.string(),
    created: z.number(),
    dueDate: z.number().optional(),
    paidAt: z.number().optional(),
    periodStart: z.number().optional(),
    periodEnd: z.number().optional(),
    subscription: z.any().optional(),
    hostedInvoiceUrl: z.string().optional(),
    invoicePdf: z.string().optional(),
    paymentIntent: z.any().optional(),
    attemptCount: z.number(),
    nextPaymentAttempt: z.number().optional(),
    customer: z.any().optional(),
    description: z.string().optional(),
    footer: z.string().optional(),
    lines: z.array(z.any()).optional()
});
export const CustomerInvoicesResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        invoices: z.array(InvoiceResponseSchema),
        hasMore: z.boolean(),
        count: z.number(),
        customerId: z.string() // Stripe customer ID
    })
});
export const DatabaseInvoicesResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        invoices: z.array(z.any()), // Database invoice entities
        hasMore: z.boolean(),
        count: z.number()
    })
});
export const CreateInvoiceResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        invoice: InvoiceResponseSchema.optional()
    })
});
export const InvoicePaymentResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        paymentIntent: z.any().optional()
    })
});
export const InvoiceActionResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        invoice: z.object({
            id: z.string(),
            status: z.string(),
            voidedAt: z.number().optional(),
            markedUncollectibleAt: z.number().optional()
        })
    })
});
export const CreatePaymentIntentResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        paymentIntent: z.object({
            id: z.string(),
            clientSecret: z.string(),
            amount: z.string(),
            currency: z.string(),
            status: z.string(),
            description: z.string().optional()
        })
    })
});
export const InvoiceWebhookResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        eventId: z.string().optional()
    })
});
export const GetInvoiceResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        invoice: InvoiceResponseSchema
    })
});
export const PaymentMethodSchema = z.object({
    id: z.string(),
    type: z.string(),
    card: z.object({
        brand: z.string(),
        country: z.string().nullable(),
        expMonth: z.number(),
        expYear: z.number(),
        funding: z.string(),
        last4: z.string(),
        fingerprint: z.string().optional(),
    }).nullable(),
    bankAccount: z.object({
        bankName: z.string().optional(),
        last4: z.string(),
        accountType: z.enum(['checking', 'savings']).optional(),
        accountHolderType: z.enum(['individual', 'company']).optional(),
        routingNumber: z.string().optional(),
    }).optional(),
    customer: z.string().optional(),
    created: z.number().optional(),
    isDefault: z.boolean().optional(), // Whether this is the default payment method
});
export const PaymentMethodResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        paymentMethods: PaymentMethodSchema,
    })
});
export const PaymentMethodsResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        paymentMethods: z.array(PaymentMethodSchema),
    })
});
export const AttachPaymentMethodResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        paymentMethod: PaymentMethodSchema
    })
});
export const DetachPaymentMethodResponseSchema = z.object({
    success: z.boolean(),
    message: z.string()
});
export const CustomerPaymentMethodsResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({
        paymentMethods: z.array(PaymentMethodSchema),
        hasMore: z.boolean(),
        count: z.number()
    })
});
export const PaymentIntentResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        paymentIntent: z.object({
            id: z.string(),
            clientSecret: z.string(),
            status: z.string(),
            paymentMethodTypes: z.array(z.string()),
            usage: z.string(),
        })
    })
});
// Note: Old SubscriptionPlan response schemas removed - use unified Subscription schemas
export const SubscriptionResponseSchema = z.object({
    id: z.string(), // Stripe subscription ID
    customerId: z.string(), // Stripe customer ID (e.g., cus_xxxxx)
    planId: z.string(),
    status: z.string(),
    currentPeriodStart: z.number(),
    currentPeriodEnd: z.number(),
    cancelAtPeriodEnd: z.boolean(),
    canceledAt: z.number().nullable().optional(),
    trialEnd: z.number().optional(),
    trialStart: z.number().optional(),
    quantity: z.number().int(),
    metadata: z.record(z.any()).optional()
});
export const CustomerSubscriptionResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        subscription: SubscriptionResponseSchema.nullable()
    })
});
export const CustomerSubscriptionsResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        subscriptions: z.array(SubscriptionResponseSchema),
        hasMore: z.boolean(),
        count: z.number()
    })
});
export const CreateSubscriptionResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        subscription: SubscriptionResponseSchema,
        clientSecret: z.string().optional()
    })
});
// Note: CreateTenantSubscriptionResponseSchema removed - use unified Subscription schemas
export const CancelSubscriptionResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        subscription: SubscriptionResponseSchema
    })
});
export const ReactivateSubscriptionResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        subscription: z.object({
            id: z.string(),
            customerId: z.string(), // Stripe customer ID
            planId: z.string(),
            status: z.string(),
            currentPeriodStart: z.number(),
            currentPeriodEnd: z.number(),
            cancelAtPeriodEnd: z.boolean(),
            canceledAt: z.number().nullable().optional(),
            trialEnd: z.number().optional(),
            trialStart: z.number().optional(),
            quantity: z.number().int(),
            metadata: z.record(z.any()).optional()
        })
    })
});
export const UpdateSubscriptionResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        subscription: SubscriptionResponseSchema
    })
});
export const CalculateProrationResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        prorationAmount: z.number(),
        prorationDate: dateSchema,
        currency: z.string()
    })
});
export const SubscriptionLimitsResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        limits: z.object({
            containers: z.object({
                current: z.number().int(),
                max: z.number().int(),
                percentage: z.coerce.number()
            }),
            wafers: z.object({
                current: z.number().int(),
                max: z.number().int(),
                percentage: z.coerce.number()
            }),
            users: z.object({
                current: z.number().int(),
                max: z.number().int(),
                percentage: z.number()
            }),
            storage: z.object({
                currentGB: z.coerce.number(),
                maxGB: z.coerce.number(),
                percentage: z.coerce.number()
            })
        })
    })
});
// SetupIntent Response Schema
export const SetupIntentResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        clientSecret: z.string(),
        setupIntentId: z.string().optional()
    })
});
// Usage Statistics Response Schema
export const UsageStatsResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        containers: z.object({
            current: z.number().int(),
            max: z.number().int(),
            percentage: z.coerce.number()
        }),
        wafers: z.object({
            current: z.number().int(),
            max: z.number().int(),
            percentage: z.coerce.number()
        }),
        users: z.object({
            current: z.number().int(),
            max: z.number().int(),
            percentage: z.coerce.number()
        }),
        storage: z.object({
            currentGB: z.coerce.number(),
            maxGB: z.coerce.number(),
            percentage: z.coerce.number()
        }),
        apiCalls: z.object({
            current: z.number().int(),
            max: z.number().int().optional(),
            percentage: z.coerce.number()
        }).optional()
    })
});
//# sourceMappingURL=invoice.schemas.js.map